// ============================================
// COMPONENTE: Gráfico de Medicamentos
// ============================================
// Exibe medicamentos por categoria e frequência de uso
// ============================================

import { useState } from 'react';
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const CORES_CATEGORIAS: Record<string, string> = {
  'Antidepressivo': '#2196F3',
  'Ansiolítico': '#4CAF50',
  'Antipsicótico': '#FF9800',
  'Estabilizador de Humor': '#9C27B0',
  'Estimulante': '#F44336',
  'Outros': '#607D8B'
};

interface Medicamento {
  nome: string;
  categoria?: string;
  dosagem?: string;
  frequencia: number;
  data_inicio?: string;
  data_fim?: string;
}

interface Props {
  dados: Medicamento[];
}

type TipoVisualizacao = 'frequencia' | 'categoria';

export default function GraficoMedicamentos({ dados }: Props) {
  const [tipoVisualizacao, setTipoVisualizacao] = useState<TipoVisualizacao>('frequencia');

  if (!dados || dados.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-pills me-2"></i>
            Medicamentos em Uso
          </h3>
        </div>
        <div className="card-body">
          <p className="text-muted text-center py-4">
            <i className="fas fa-prescription-bottle fa-3x mb-3 d-block text-secondary"></i>
            Nenhum medicamento registrado nos prontuários
          </p>
        </div>
      </div>
    );
  }

  // Preparar dados por frequência
  const dadosFrequencia = dados
    .map(m => ({
      nome: m.nome.length > 20 ? m.nome.substring(0, 20) + '...' : m.nome,
      nomeCompleto: m.nome,
      frequencia: m.frequencia,
      dosagem: m.dosagem,
      categoria: m.categoria || 'Outros',
      cor: CORES_CATEGORIAS[m.categoria || 'Outros'] || '#607D8B'
    }))
    .sort((a, b) => b.frequencia - a.frequencia);

  // Preparar dados por categoria
  const dadosCategoria = Object.entries(
    dados.reduce((acc, m) => {
      const cat = m.categoria || 'Outros';
      acc[cat] = (acc[cat] || 0) + m.frequencia;
      return acc;
    }, {} as Record<string, number>)
  ).map(([categoria, total]) => ({
    nome: categoria,
    valor: total,
    cor: CORES_CATEGORIAS[categoria] || '#607D8B'
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border rounded p-2 shadow-sm">
          <p className="mb-1"><strong>{data.nomeCompleto || data.nome}</strong></p>
          {data.dosagem && <p className="mb-0 small text-muted">Dosagem: {data.dosagem}</p>}
          {data.categoria && <p className="mb-0 small text-muted">Categoria: {data.categoria}</p>}
          <p className="mb-0 text-primary">
            {tipoVisualizacao === 'frequencia' ? `Registrado em ${data.frequencia} prontuário(s)` : `Total: ${data.valor}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
        <h3 className="card-title mb-0">
          <i className="fas fa-pills me-2"></i>
          Medicamentos em Uso
        </h3>
        <div className="btn-group btn-group-sm">
          <button
            className={`btn ${tipoVisualizacao === 'frequencia' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTipoVisualizacao('frequencia')}
          >
            <i className="fas fa-chart-bar me-1"></i>
            Por Frequência
          </button>
          <button
            className={`btn ${tipoVisualizacao === 'categoria' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTipoVisualizacao('categoria')}
          >
            <i className="fas fa-chart-pie me-1"></i>
            Por Categoria
          </button>
        </div>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={350}>
          {tipoVisualizacao === 'frequencia' ? (
            <BarChart data={dadosFrequencia} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="nome" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                interval={0}
                tick={{ fontSize: 10 }}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="frequencia" 
                name="Frequência" 
                radius={[4, 4, 0, 0]}
              >
                {dadosFrequencia.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.cor} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <PieChart>
              <Pie
                data={dadosCategoria}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="valor"
                nameKey="nome"
                label={({ nome, valor }: { nome: string; valor: number }) => `${nome}: ${valor}`}
              >
                {dadosCategoria.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.cor} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          )}
        </ResponsiveContainer>
        
        {/* Legenda de categorias */}
        <div className="mt-3 border-top pt-3">
          <small className="text-muted d-block mb-2">
            <i className="fas fa-palette me-1"></i>
            Legenda de Categorias:
          </small>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(CORES_CATEGORIAS).map(([categoria, cor]) => (
              <span key={categoria} className="badge" style={{ backgroundColor: cor }}>
                {categoria}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
